import type { CustomerInfo, Package } from "@revenuecat/purchases-js";
import apiClient from "./apiClient";
import { setUser, getUser } from "../store/auth/storeToken";
import type { BillingCycle, PlanKey } from "../screen/PricingPage/plans";

// Web subscriptions run on RevenueCat Web Billing, so the app, the website
// and the backend all see the same subscription. The public key is safe in
// the browser; without it the site keeps sending people to the app.
const API_KEY = process.env.REACT_APP_REVENUECAT_WEB_BILLING_KEY || "";
export const webBillingEnabled = API_KEY.length > 0;

export type PaidPlanKey = Exclude<PlanKey, "free">;

export interface WebPlan {
  tier: PaidPlanKey;
  cycle: BillingCycle;
  rcPackage: Package;
  trialDays: number | null;
}

// The SDK is large, so it loads only when a signed-in visitor needs it,
// keeping it out of the bundle every ad visitor downloads.
const loadSdk = () => import("@revenuecat/purchases-js");

const purchasesFor = async (userId: string) => {
  const { Purchases } = await loadSdk();
  if (!Purchases.isConfigured()) {
    return Purchases.configure({ apiKey: API_KEY, appUserId: userId });
  }
  const purchases = Purchases.getSharedInstance();
  if (purchases.getAppUserId() !== userId) await purchases.changeUser(userId);
  return purchases;
};

// PeriodUnit values, as strings so this file doesn't load the SDK.
const DAYS_PER_UNIT: Record<string, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
};

// Same naming rule as the mobile app: a product with "lite" in it is Lite,
// anything else is Pro.
export const toWebPlan = (rcPackage: Package): WebPlan | null => {
  const product = rcPackage.webBillingProduct;
  const name = `${rcPackage.identifier} ${product.identifier}`.toLowerCase();
  const unit = String(product.period?.unit ?? "");
  const cycle: BillingCycle | null =
    unit === "year" ? "annual" : unit === "month" ? "monthly" : null;
  if (!cycle) return null;

  const trial = product.defaultSubscriptionOption?.trial?.period;
  return {
    tier: name.includes("lite") ? "lite" : "pro",
    cycle,
    rcPackage,
    trialDays: trial ? trial.number * (DAYS_PER_UNIT[String(trial.unit)] ?? 0) || null : null,
  };
};

export const planKey = (tier: PaidPlanKey, cycle: BillingCycle) => `${tier}:${cycle}`;

/** The plans for sale on the web, keyed by planKey(tier, cycle). */
export const loadWebPlans = async (userId: string) => {
  const purchases = await purchasesFor(userId);
  const offerings = await purchases.getOfferings();
  const plans: Record<string, WebPlan> = {};
  for (const rcPackage of offerings.current?.availablePackages ?? []) {
    const plan = toWebPlan(rcPackage);
    if (plan) plans[planKey(plan.tier, plan.cycle)] = plan;
  }
  return plans;
};

// The backend reads RevenueCat itself when it can; the CustomerInfo is only
// a fallback for older server setups.
const toSyncPayload = (info: CustomerInfo) => ({
  subscriber: {
    entitlements: {
      active: Object.fromEntries(
        Object.entries(info.entitlements.active).map(([id, e]) => [
          id,
          {
            isActive: e.isActive,
            willRenew: e.willRenew,
            productIdentifier: e.productIdentifier,
            expirationDate: e.expirationDate?.toISOString() ?? null,
          },
        ])
      ),
    },
  },
});

/** Saves the latest plan on the server and in the stored user. */
export const refreshSubscription = async (userId: string) => {
  const purchases = await purchasesFor(userId);
  const info = await purchases.getCustomerInfo();
  await apiClient.post("/revenuecat/sync", toSyncPayload(info));
  const { data: me } = await apiClient.get("/auth/me");
  const stored = getUser() || {};
  setUser({
    ...stored,
    tier: me.tier,
    isPaid: me.isPaid,
    subscriptionInterval: me.subscriptionInterval ?? null,
    subscriptionAutoRenew: me.subscriptionAutoRenew ?? null,
  });
  return info;
};

/**
 * Opens RevenueCat's checkout. Resolves "cancelled" when the buyer closes it.
 */
export const purchaseWebPlan = async (
  userId: string,
  email: string,
  plan: WebPlan
): Promise<"purchased" | "cancelled"> => {
  const purchases = await purchasesFor(userId);
  const { ErrorCode, PurchasesError } = await loadSdk();
  try {
    await purchases.purchase({ rcPackage: plan.rcPackage, customerEmail: email });
  } catch (error) {
    if (
      error instanceof PurchasesError &&
      error.errorCode === ErrorCode.UserCancelledError
    ) {
      return "cancelled";
    }
    throw error;
  }
  await refreshSubscription(userId);
  return "purchased";
};

export interface SubscriptionStatus {
  tier: PaidPlanKey;
  store: string;
  trial: boolean;
  willRenew: boolean;
  expiresAt: Date | null;
  billingIssue: boolean;
}

/** The customer's best active plan as RevenueCat reports it, or null. */
export const describeSubscription = (info: CustomerInfo): SubscriptionStatus | null => {
  const active = Object.values(info.entitlements.active);
  const best =
    active.find((e) => e.identifier === "pro" || e.identifier === "elite") ??
    active.find((e) => e.identifier === "lite");
  if (!best) return null;
  return {
    tier: best.identifier === "lite" ? "lite" : "pro",
    store: best.store,
    trial: best.periodType === "trial",
    willRenew: best.willRenew,
    expiresAt: best.expirationDate,
    billingIssue: !!best.billingIssueDetectedAt,
  };
};

/**
 * Where this customer manages their plan: the web billing portal for web
 * subscriptions, or the App Store / Google Play page for app ones.
 */
export const getManagementUrl = async (userId: string) => {
  const purchases = await purchasesFor(userId);
  const info = await purchases.getCustomerInfo();
  return info.managementURL;
};

// A visitor who picks a paid plan before having an account signs up first;
// this remembers the plan so checkout opens once they're logged in.
const PENDING_PLAN_KEY = "vo_pending_plan";

export const savePendingPlan = (tier: PaidPlanKey, cycle: BillingCycle) => {
  try {
    sessionStorage.setItem(PENDING_PLAN_KEY, JSON.stringify({ tier, cycle }));
  } catch {
    // storage blocked: they can pick the plan again after signing up
  }
};

export const takePendingPlan = (): { tier: PaidPlanKey; cycle: BillingCycle } | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PLAN_KEY);
    sessionStorage.removeItem(PENDING_PLAN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const hasPendingPlan = () => {
  try {
    return !!sessionStorage.getItem(PENDING_PLAN_KEY);
  } catch {
    return false;
  }
};
